/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import Button, {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS
} from "@/components/Button/Button";
import { useAuth } from "@/context/Auth/AuthProvider";
import { USER_PERMISSIONS } from "@/roles/types/enums";
import RectangleLoader from "@/shared/components/loaders/RectangleLoader";
import { Typography } from "@mui/material";
import Divider from "@mui/material/Divider";

import { colors } from "@dotnet/telematics-design-system";

import { useMediaQueries } from "../../../../../../../shared";
import NoSchedulesAssigned from "../NoSchedulesAssigned/NoSchedulesAssigned";
import { useStyles } from "../styles";
import { CONDITION_FAILURE_REASONS, ELIGIBILITY_CONDITIONS } from "../types";
import {
  DownloadMonitoringVehicleData,
  EligibilityCondition
} from "../types/interfaces";
import RequirementsSection from "./RequirementsSection/RequirementsSection";
import ScheduleStatusCards from "./ScheduleStatusCards/ScheduleStatusCards";

interface DownloadMonitoringDialogContentProps {
  schedulesExists: boolean;
  isLiveDataLoading: boolean;
  liveStatusData: DownloadMonitoringVehicleData;
  handleOpenHistory: (isOpen: boolean) => void;
  handleOpenAssignSchedules: (isOpen: boolean) => void;
}

const DownloadMonitoringDialogContent = ({
  schedulesExists,
  isLiveDataLoading,
  liveStatusData,
  handleOpenHistory,
  handleOpenAssignSchedules
}: DownloadMonitoringDialogContentProps) => {
  const { t } = useTranslation();

  const { fromMd } = useMediaQueries();

  const { getAccessStatus } = useAuth();

  const canSeeCreditsRequirement = getAccessStatus(
    USER_PERMISSIONS.SeeCreditsDashboard
  );

  const canManageSchedules = getAccessStatus(USER_PERMISSIONS.ManageSchedules);

  const { downloadMonitoringContent, containerStyle } = useStyles({
    fromMd
  });

  const hasNoSchedulesAssigned = liveStatusData?.schedules?.isEmpty;

  const conditions: EligibilityCondition[] =
    liveStatusData?.eligibility?.conditions ?? [];

  const creditsReason = conditions.find(
    item => item.condition === ELIGIBILITY_CONDITIONS.CreditsAvailable
  )?.reason;

  const showBanner =
    !canSeeCreditsRequirement &&
    creditsReason === CONDITION_FAILURE_REASONS.InsufficientCredits;

  const renderSchedules = () => {
    if (isLiveDataLoading) {
      return <RectangleLoader height="288px" width="100%" />;
    }

    return hasNoSchedulesAssigned ? (
      <NoSchedulesAssigned
        schedulesExists={schedulesExists}
        canManageSchedules={canManageSchedules}
        handleClick={() => handleOpenAssignSchedules(true)}
      />
    ) : (
      <ScheduleStatusCards
        liveStatusData={liveStatusData}
        isBlocked={showBanner}
      />
    );
  };

  return (
    <div>
      {showBanner && (
        <BannerMessage
          data-testid="download-blocked-banner"
          status={BANNER_MESSAGE_STATUSES.Critical}
          title={t("Dialog##download blocked message")}
          externalCustomStyle={{ marginBottom: "16px" }}
        />
      )}
      <div css={css(downloadMonitoringContent)}>
        <div css={css(containerStyle)}>
          <Typography variant="body3Medium" color={colors.textPlaceholder}>
            {t("General##requirements")}
          </Typography>

          <RequirementsSection
            conditions={conditions}
            showLoader={isLiveDataLoading}
            canSeeCreditsRequirement={canSeeCreditsRequirement}
          />
        </div>

        <Divider
          orientation={fromMd ? "vertical" : "horizontal"}
          flexItem={fromMd}
        />

        <div css={css(containerStyle)}>
          <Typography variant="body3Medium" color={colors.textPlaceholder}>
            {t("General##schedules")}
          </Typography>

          {renderSchedules()}

          <Button
            size={BUTTON_SIZES.Normal}
            color={BUTTON_COLORS.PrimarySubtle}
            variant={BUTTON_VARIANTS.TextOnly}
            onClick={() => handleOpenHistory(true)}
            css={css({
              marginTop: "auto"
            })}
          >
            {t("Button##history")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DownloadMonitoringDialogContent;
